import { type FC, type ReactNode, useState } from "react";
import { X, Info, CircleCheck, TriangleAlert, CircleAlert } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { IconButton } from "@/shared/components/ui/icon-button";

export type CalloutVariant = "neutral" | "info" | "brand" | "warning" | "error";

interface CalloutProps {
  variant?: CalloutVariant;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  hideIcon?: boolean;
  actions?: ReactNode;
  trailing?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const variantStyles: Record<CalloutVariant, { container: string; icon: string }> = {
  neutral: { container: "border-primary bg-subtle", icon: "text-secondary" },
  info: { container: "border-blue-500 bg-blue-50", icon: "text-secondary" },
  brand: { container: "border-brand bg-brand-subtle", icon: "text-brand" },
  warning: { container: "border-alert bg-yellow-50", icon: "text-warning" },
  error: { container: "border-destructive bg-destructive-light", icon: "text-destructive" },
};

const defaultIcons: Record<CalloutVariant, ReactNode> = {
  neutral: <Info className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
  brand: <CircleCheck className="h-4 w-4" />,
  warning: <TriangleAlert className="h-4 w-4" />,
  error: <CircleAlert className="h-4 w-4" />,
};

export const Callout: FC<CalloutProps> = ({
  variant = "neutral",
  title,
  children,
  icon,
  hideIcon = false,
  actions,
  trailing,
  onDismiss,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const styles = variantStyles[variant];

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  return isVisible ? (
    <div
      className={cn(
        "flex gap-3 rounded-lg border p-4",
        trailing ? "flex-col sm:flex-row sm:items-center" : "items-start",
        styles.container,
        className,
      )}
    >
      <div className={cn("flex min-w-0 flex-1 gap-3", trailing ? "items-center" : "items-start")}>
        {hideIcon ? null : (
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-primary bg-white"
            aria-hidden="true"
          >
            <span className={styles.icon}>{icon ?? defaultIcons[variant]}</span>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {title ? <p className="text-sm font-semibold leading-[1.55]">{title}</p> : null}
          {children ? (
            <div className="text-sm leading-[1.55] text-secondary">{children}</div>
          ) : null}
          {actions ? <div className="mt-2 flex items-center gap-4">{actions}</div> : null}
        </div>
      </div>

      {trailing ? <div className="shrink-0 sm:ml-auto">{trailing}</div> : null}

      {onDismiss ? (
        <IconButton onClick={handleDismiss} className="mt-1" label="Dismiss" tooltip={false}>
          <X />
        </IconButton>
      ) : null}
    </div>
  ) : null;
};
